import {
  isEditPermissionRequiredForAddRow,
  isReadOnlyColumn,
  TableFullPermissionFormData,
  TablePermissionsFormData,
} from '@doss/common-all'
import Bluebird from 'bluebird'
import type { TOptions } from 'i18next'
import { get, isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useHotkeys } from 'react-hotkeys-hook'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { Button } from '~/@/components/ui/button'
import Typography from '~/@/components/ui/typography'
import {
  PermissionType,
  RoleType,
  useGetOrganizationRolesWithTablePermissionsQuery,
  useGetTableColumnsTabsChildTablesQuery,
  useUpdateRoleTablePermissionsMutation,
} from '~/common/generated/apollo-operations'
import type { i18NextKeys } from '~/common/generated/i18n.generated'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { localKeyMap } from '~/common/utils/keymap'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { RightPanelState } from '../table-settings/types'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { ColumnFormValues } from './types'
import {
  computeFieldPermissionChange,
  convertFormDataToPermissionInput,
  convertTablePermissionsToMap,
  getFieldPermissionRoleImpact,
  isCompleteRoleTablePermissions,
  RolePermissionsMap,
} from './utils'

// Stable order for warning bullets when multiple table-level flags drop (not object key order).
const REMOVED_TABLE_PERM_ORDER = ['addRow', 'configurePivotTables'] as const

const REMOVED_PERMISSION_LABEL_KEYS: Record<
  (typeof REMOVED_TABLE_PERM_ORDER)[number],
  | 'user.roles.field_permissions.warning_removed_add_row'
  | 'user.roles.field_permissions.warning_removed_configure_pivot_tables'
> = {
  addRow: 'user.roles.field_permissions.warning_removed_add_row',
  configurePivotTables: 'user.roles.field_permissions.warning_removed_configure_pivot_tables',
}

function getOrderedRemovedTablePermissionLabels(
  removed: (keyof TablePermissionsFormData)[],
  t: (key: i18NextKeys, options?: TOptions) => string,
): string[] {
  return REMOVED_TABLE_PERM_ORDER.filter((k) => removed.includes(k)).map((k) =>
    t(REMOVED_PERMISSION_LABEL_KEYS[k]),
  )
}

/** Whether to show the child-table inheritance note for this impact (parent save cascades to children). */
function childTableCascadeApplies(impact: {
  losesRequiredColumnRowOps: boolean
  removedTablePermissions: (keyof TablePermissionsFormData)[]
}): boolean {
  if (impact.losesRequiredColumnRowOps) {
    return true
  }
  return impact.removedTablePermissions.some((k) => k === 'addRow' || k === 'configurePivotTables')
}

interface FieldPermissionsTabProps {
  tableColumn: Exclude<ColumnFormValues, 'id'> & { id: string }
  setRightPanelState?: (panelState: RightPanelState | null) => void
  tableId: string
}

/**
 * Renders the permissions tab for a specific field/column in the table settings
 */
export function FieldPermissionsTab({
  tableColumn,
  setRightPanelState,
  tableId,
}: FieldPermissionsTabProps) {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const { data: rolesData, loading } = useGetOrganizationRolesWithTablePermissionsQuery({
    variables: {
      tableId,
    },
  })
  const { data: tableData, loading: tableLoading } = useGetTableColumnsTabsChildTablesQuery({
    variables: {
      tableId,
    },
  })
  const [updateRoleTablePermissions] = useUpdateRoleTablePermissionsMutation()
  const [hasInitialized, setHasInitialized] = useState(false)

  const form = useForm<RolePermissionsMap>({
    mode: 'onChange',
    defaultValues: {},
  })

  // Server-shaped permissions per role/table: baseline for dirty checks and `computeFieldPermissionChange`.
  const rolePermissionsMap = useMemo(() => {
    if (!tableData?.table) {
      return {}
    }
    const tablePermissionsMap = convertTablePermissionsToMap(
      rolesData?.roles ?? [],
      tableData.table,
    )
    return tablePermissionsMap
  }, [rolesData?.roles, tableData?.table])

  // Do not display the edit checkbox for columns that are always read only. Excluding columns with
  // isReadOnly = true because those can still be editable at add record
  const isReadOnly = useMemo(
    () => isReadOnlyColumn(tableColumn.type, tableColumn.indexAutogenerate),
    [tableColumn.type, tableColumn.indexAutogenerate],
  )

  const editPermissionRequiredForRowOps = useMemo(
    () =>
      isEditPermissionRequiredForAddRow(
        tableColumn.type,
        tableColumn.indexAutogenerate,
        tableColumn.isNullable,
      ),
    [tableColumn.type, tableColumn.indexAutogenerate, tableColumn.isNullable],
  )

  // Current form values for every role (drives row checkboxes and impact warnings).
  const watchedPermissions = useWatch({
    control: form.control,
  })

  const editableRoles = useMemo(() => {
    return rolesData?.roles.filter((role) => role.roleType === RoleType.MEMBER) ?? []
  }, [rolesData?.roles])

  useEffect(() => {
    const hasData = Object.keys(rolePermissionsMap).length > 0

    if (hasData && !hasInitialized) {
      form.reset(rolePermissionsMap)
      setHasInitialized(true)
    }
  }, [rolePermissionsMap, form, hasInitialized])

  // Applies one checkbox change for a member role: merges column + table-level side effects via
  // `computeFieldPermissionChange`, using the loaded snapshot in `rolePermissionsMap` for full reverts.
  const handlePermissionChange = (
    roleId: string,
    permissionType: PermissionType.VIEW_COLUMN | PermissionType.EDIT_CELL,
    checked: boolean,
  ) => {
    const currentPermissions = form.getValues(roleId)
    if (!isCompleteRoleTablePermissions(currentPermissions)) {
      return
    }

    const initialForRole = rolePermissionsMap[roleId]
    const updatedPermission = computeFieldPermissionChange(
      currentPermissions,
      tableColumn.id,
      permissionType,
      checked,
      editPermissionRequiredForRowOps,
      initialForRole,
    )
    form.setValue(roleId, updatedPermission)
  }

  // Member roles whose full table permission object differs from the last-loaded server state.
  // Drives save scope, submit, and which roles receive impact warnings (only dirty roles are analyzed).
  const changedRoles = useMemo(() => {
    if (Object.keys(rolePermissionsMap).length === 0) {
      return []
    }

    return editableRoles
      .filter((role) => {
        const currentRolePermissions = watchedPermissions[role.id]
        const initialRolePermissions = rolePermissionsMap[role.id]

        return !isEqual(currentRolePermissions, initialRolePermissions)
      })
      .map((role) => role.id)
  }, [editableRoles, watchedPermissions, rolePermissionsMap])

  // Enables save when any member role has pending permission changes.
  const isDirty = useMemo(() => {
    return changedRoles.length > 0
  }, [changedRoles])

  const resolvedTable = tableData?.table

  // Linked child tables for this table (used for the inheritance banner when parent-level grants drop).
  const childTables = useMemo(() => {
    if (!resolvedTable) {
      return []
    }
    return resolvedTable.childTables
  }, [resolvedTable])

  const hasChildTables = childTables.length > 0

  const tableDisplayName = resolvedTable?.name ?? ''

  // For each dirty role, compares baseline vs current form state and collects non-empty warnings:
  // losing add-row on a required column, and/or table-level flags removed (e.g. pivot) by this edit.
  // Rows feed the impact panel below the role list; roles with no meaningful diff are omitted.
  const rolePermissionImpacts = useMemo(() => {
    if (changedRoles.length === 0) {
      return []
    }

    const rows: Array<{
      roleId: string
      roleName: string
      impact: NonNullable<ReturnType<typeof getFieldPermissionRoleImpact>>
    }> = []

    for (const roleId of changedRoles) {
      const before = rolePermissionsMap[roleId]
      const afterRaw = watchedPermissions[roleId]
      if (!isCompleteRoleTablePermissions(before) || !isCompleteRoleTablePermissions(afterRaw)) {
        continue
      }

      const impact = getFieldPermissionRoleImpact(before, afterRaw as TableFullPermissionFormData, {
        columnId: tableColumn.id,
        editPermissionRequiredForRowOps,
      })
      if (!impact) {
        continue
      }

      const role = editableRoles.find((r) => r.id === roleId)
      rows.push({
        roleId,
        roleName: role?.name ?? roleId,
        impact,
      })
    }

    return rows
  }, [
    changedRoles,
    rolePermissionsMap,
    watchedPermissions,
    tableColumn.id,
    editPermissionRequiredForRowOps,
    editableRoles,
  ])

  const handleSubmit = async (values: RolePermissionsMap) => {
    try {
      await Bluebird.map(changedRoles, async (roleId) => {
        const permissions = get(values, roleId, undefined)
        if (permissions) {
          await updateRoleTablePermissions({
            variables: {
              input: {
                roleId,
                tableId,
                permissions: convertFormDataToPermissionInput(tableId, permissions),
              },
              tableId,
            },
          })
        }
      })
      snackbar.showSuccess(t('user.roles.permissions_updated_successfully'))
      setRightPanelState?.(null)
    } catch {
      snackbar.showError(t('user.roles.failed_to_update_permissions'))
    }

    return
  }

  const ref = useHotkeys<HTMLFormElement>(
    [localKeyMap.CONTROL_ENTER, localKeyMap.COMMAND_ENTER, localKeyMap.ENTER, localKeyMap.ESC],
    (event) => {
      if (event.key === localKeyMap.ESC) {
        form.reset()
        if (setRightPanelState) {
          setRightPanelState(null)
        }

        return
      }

      if (event.key === localKeyMap.ENTER) {
        event.preventDefault()

        if (!event.ctrlKey && !event.metaKey) {
          return
        }

        form.handleSubmit(handleSubmit)
      }
    },
    { enableOnFormTags: true },
  )

  if (loading || tableLoading) {
    return (
      <div className="p-4">
        <LoadingSpinner type="navigation" />
      </div>
    )
  }

  if (!rolesData?.roles.length) {
    return (
      <div className="p-4">
        <Typography className="text-gray-8" variant="body2">
          {t('user.roles.no_non_admin_roles_found')}
        </Typography>
      </div>
    )
  }

  return (
    <form
      ref={ref}
      className="flex size-full flex-col overflow-hidden"
      onSubmit={form.handleSubmit(handleSubmit)}
      tabIndex={-1}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2">
          <Typography variant="body2">{tableColumn.name}</Typography>
        </div>
        <div className="flex items-center border-y border-gray-4 bg-gray-2 px-4 py-2">
          <Typography className="w-[200px] px-2" variant="body2">
            {t('user.users_table.roles')}
          </Typography>
          <Typography className="flex w-[120px] justify-center px-2" variant="body2">
            {t('table.view')}
          </Typography>
          {!isReadOnly && (
            <Typography className="flex w-[120px] justify-center px-2" variant="body2">
              {t('table.edit')}
            </Typography>
          )}
          {isReadOnly && (
            <Typography
              className="flex w-[120px] justify-center px-2 italic text-gray-8"
              variant="body2"
            >
              {t('table.create_column.read_only')}
            </Typography>
          )}
        </div>

        {/* Roles list and save warnings share one scroll region */}
        <div className="min-h-0 flex-1 overflow-auto">
          {editableRoles.map((role) => {
            const currentPermissions = get(watchedPermissions, role.id, undefined)

            const columnPermissions = currentPermissions?.columnPermissions?.[tableColumn.id]
            if (!columnPermissions) {
              return null
            }

            return (
              <div
                key={role.id}
                className="flex items-center border-b border-gray-4 px-4 py-2 hover:bg-gray-1"
              >
                <Typography className="w-[200px] truncate px-2" variant="body2">
                  {role.name}
                </Typography>
                <div className="flex w-[120px] justify-center px-2">
                  <Checkbox
                    checked={columnPermissions.view ?? false}
                    onCheckedChange={(checked) =>
                      handlePermissionChange(role.id, PermissionType.VIEW_COLUMN, checked === true)
                    }
                    variant="secondary"
                  />
                </div>
                <div className="flex w-[120px] justify-center px-2">
                  {!isReadOnly && (
                    <Checkbox
                      checked={columnPermissions.edit ?? false}
                      onCheckedChange={(checked) =>
                        handlePermissionChange(role.id, PermissionType.EDIT_CELL, checked === true)
                      }
                      variant="secondary"
                    />
                  )}
                </div>
              </div>
            )
          })}
          {rolePermissionImpacts.length > 0 && (
            <div className="border-t border-gray-4">
              <div className="p-4 pt-3">
                <p className="mb-4 text-xs leading-snug text-gray-9">
                  {t('user.roles.field_permissions.impact_banner')}
                </p>
                <div className="flex flex-col gap-4">
                  {rolePermissionImpacts.map(({ roleId, roleName, impact }) => {
                    const removedLabels = getOrderedRemovedTablePermissionLabels(
                      impact.removedTablePermissions,
                      t,
                    )
                    const showChildCascade = hasChildTables && childTableCascadeApplies(impact)
                    const childNames = childTables.map((c) => c.name).join(', ')

                    return (
                      <div
                        key={roleId}
                        className="flex flex-col gap-3 rounded-md border border-red-5 bg-red-2 p-4"
                      >
                        <div className="text-sm font-semibold leading-tight text-gray-11">
                          {roleName}
                        </div>
                        <div className="flex flex-col gap-3 text-xs leading-snug">
                          {impact.losesRequiredColumnRowOps && (
                            <p className="text-gray-10">
                              {t('user.roles.field_permissions.warning_required_column_edit')}
                            </p>
                          )}
                          {removedLabels.length > 0 && (
                            <div>
                              <div className="mb-2 font-medium text-gray-10">
                                {t('user.roles.field_permissions.scope_this_table', {
                                  tableName: tableDisplayName,
                                })}
                              </div>
                              <div className="flex flex-col gap-2">
                                {removedLabels.map((label) => (
                                  <div
                                    key={`${roleId}-${label}`}
                                    className="border-s-2 border-gray-5 ps-2.5 text-gray-9"
                                  >
                                    {label}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {showChildCascade && (
                            <p className="border-t border-gray-4 pt-3 text-gray-10">
                              {t('user.roles.field_permissions.warning_child_tables_inherit', {
                                childNames,
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-gray-4 p-4">
        <Button
          onClick={() => {
            if (setRightPanelState) {
              setRightPanelState(null)
            }
            form.reset()
          }}
          type="button"
          variant="outline"
        >
          {t('common.actions.cancel')}
        </Button>
        <Button disabled={form.formState.isSubmitting || !isDirty} type="submit">
          {t('common.actions.save')} ⌘↵
        </Button>
      </div>
    </form>
  )
}
